import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { ShieldAlert, ArrowLeftRight } from "lucide-react";
import { PageHead } from "@/components/PageHead";
import { QuoteGrid, useQuotes } from "@/components/Quotes";
import { Glossary } from "@/components/Glossary";
import { PlatformCards } from "@/components/PlatformCards";

const TABS = [["forex", "Forex"], ["commodities", "Commodities"], ["crypto", "Crypto"]];

const Converter = () => {
  const { data } = useQuotes("forex");
  const items = data?.items || [];
  const [sym, setSym] = useState("USDINR=X");
  const [amt, setAmt] = useState(1000);
  const q = items.find((x) => x.symbol === sym);
  return (
    <div data-testid="forex-converter" className="mt-6 rounded-2xl bg-[#1E293B] text-white p-6 flex flex-wrap items-end gap-4">
      <ArrowLeftRight className="w-6 h-6 text-amber-300" />
      <label className="text-xs text-slate-400">Amount<input data-testid="forex-amount" type="number" value={amt} onChange={(e) => setAmt(Number(e.target.value))} className="block mt-1 w-32 h-11 rounded-lg bg-white text-slate-900 px-3 font-mono" /></label>
      <label className="text-xs text-slate-400">Currency<select data-testid="forex-currency" value={sym} onChange={(e) => setSym(e.target.value)} className="block mt-1 h-11 rounded-lg bg-white text-slate-900 px-3">{items.map((x) => <option key={x.symbol} value={x.symbol}>{x.label.split(" / ")[0]}</option>)}</select></label>
      <p className="font-mono text-2xl font-bold">= ₹{q ? (amt * q.price).toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—"}</p>
      <p className="text-xs text-slate-400 basis-full">Interbank mid-rate. Banks and forex cards add 1–3% markup.</p>
    </div>
  );
};

export default function Markets() {
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "forex");
  useEffect(() => { if (sp.get("tab")) setTab(sp.get("tab")); }, [sp]);
  return (
    <div data-testid="markets-page">
      <PageHead eyebrow="Forex · Commodities · Crypto" title="Beyond stocks & funds — live." sub="Track the rupee, gold, crude and crypto in real time, understand the rules, and know where to invest safely." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="inline-flex rounded-xl bg-slate-200/70 p-1 mb-6">
          {TABS.map(([k, l]) => <button key={k} data-testid={`markets-tab-${k}`} onClick={() => setTab(k)} className={`h-10 px-5 rounded-lg text-sm font-bold ${tab === k ? "bg-white shadow" : "text-slate-500"}`}>{l}</button>)}
        </div>
        {tab === "forex" && <><QuoteGrid group="forex" prefix="₹" dp={3} /><Converter /></>}
        {tab === "commodities" && (
          <>
            <QuoteGrid group="commodities" prefix="$" />
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <Link to="/mutual-funds?category=Gold%20ETF" data-testid="markets-gold-etf" className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-6 hover:bg-amber-100 transition-colors"><p className="font-display font-bold text-lg">Invest in gold via Gold ETFs →</p><p className="text-sm text-slate-600 mt-1">Compare live AMFI data on all Gold ETFs — no lockers, no making charges.</p></Link>
              <Link to="/mutual-funds?category=Silver%20ETF" data-testid="markets-silver-etf" className="rounded-2xl border-2 border-slate-300 bg-white p-6 hover:bg-slate-50 transition-colors"><p className="font-display font-bold text-lg">Invest in silver via Silver ETFs →</p><p className="text-sm text-slate-600 mt-1">Track and compare Silver ETFs on the same live data.</p></Link>
            </div>
          </>
        )}
        {tab === "crypto" && (
          <>
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-5 flex gap-3"><ShieldAlert className="w-6 h-6 text-red-500 shrink-0" /><p className="text-sm text-red-800"><b>High risk, unregulated.</b> Crypto is not legal tender in India. Gains are taxed at 30% + 1% TDS. Use only FIU-IND registered exchanges and never invest borrowed money.</p></div>
            <QuoteGrid group="crypto" prefix="₹" />
          </>
        )}
        <PlatformCards key={tab} segment={tab} />
        <Glossary set={tab} />
      </div>
    </div>
  );
}
